import { useState } from 'react';
import {
  Alert, Box, Button, Card, Chip, CircularProgress, InputAdornment, Tab, Tabs, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography, alpha,
} from '@mui/material';
import type { AxiosError } from 'axios';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../components/ToastProvider';
import { errMessage } from '../../utils/errMessage';
import { PageHeader } from '../../components/PageHeader';
import { ConfirmDialog } from '../../components/ConfirmDialog';

/** No realtime socket — orders/requests are polled so fills and approvals show up promptly. */
const POLL_MS = 5_000;

interface Order {
  id: number; symbol: string; market: string; side: string; order_type: string; status: string;
  quantity: string; filled_quantity: string; avg_fill_price: string | null; leverage: number | null; created_at: string;
  error_code: string | null; error_message: string | null;
}

interface RequestRow {
  id: number; symbol: string; market: string; side: string; order_type: string; status: string;
  quantity: string; price: string | null; reject_reason: string | null; created_at: string; reviewed_at: string | null;
}

const statusColor: Record<string, string> = {
  // executed_orders
  FILLED: '#16c784', PARTIALLY_FILLED: '#f5a623', SUBMITTING: '#f5a623', NEW: '#4f8cff',
  FAILED: '#ea3943', REJECTED: '#ea3943', CANCELLED: '#8b97b0', EXPIRED: '#8b97b0',
  // pending_orders (approval flow)
  PENDING: '#f5a623', APPROVED: '#16c784',
};

const sc = (s: string) => statusColor[s] ?? '#8b97b0';

/** Trim trailing zeros from a DECIMAL(36,18) string for display, without precision loss. */
const fmtNum = (v: string | number | null | undefined): string => {
  if (v === null || v === undefined || v === '') return '—';
  const s = String(v);
  if (!s.includes('.')) return s;
  const t = s.replace(/0+$/, '').replace(/\.$/, '');
  return t === '' ? '0' : t;
};

function StatusChip({ status, title }: { status: string; title?: string }) {
  const c = sc(status);
  const chip = (
    <Chip size="small" label={status}
      sx={{ color: c, backgroundColor: alpha(c, 0.14), border: `1px solid ${alpha(c, 0.35)}` }} />
  );
  return title ? <Tooltip title={title}><span>{chip}</span></Tooltip> : chip;
}

export default function OrdersPage() {
  const { isViewOnly } = useAuth();
  const toast = useToast();
  const [tab, setTab] = useState(0);
  const [query, setQuery] = useState('');
  // Order awaiting cancel confirmation (null = dialog closed).
  const [pendingCancel, setPendingCancel] = useState<Order | null>(null);

  const { data: orders, refetch: refetchOrders } = useQuery({
    queryKey: ['orders'],
    queryFn: async () => (await api.get('/trading/orders')).data.data.items as Order[],
    refetchInterval: POLL_MS,
  });

  const { data: requests } = useQuery({
    queryKey: ['requests'],
    queryFn: async () => (await api.get('/trading/requests')).data.data.items as RequestRow[],
    refetchInterval: POLL_MS,
  });

  const cancel = useMutation({
    mutationFn: (id: number) => api.post(`/trading/orders/${id}/cancel`),
    onSuccess: () => { toast.success('Order cancelled', 'Cancelled'); refetchOrders(); },
    onError: (e) => toast.error(errMessage(e, 'Failed to cancel order'), 'Cancel failed'),
    onSettled: () => setPendingCancel(null),
  });

  // Only resting orders can be cancelled. MARKET orders fill instantly — never show Cancel.
  // View-only clients can't cancel, so the action never appears for them.
  const cancellable = (o: Order) =>
    !isViewOnly && (o.status === 'NEW' || o.status === 'PARTIALLY_FILLED') && o.order_type !== 'MARKET';

  // Free-text filter across the visible columns (symbol, market, side, type, status).
  const q = query.trim().toLowerCase();
  const matches = (...fields: (string | null | undefined)[]) =>
    !q || fields.some((f) => (f ?? '').toLowerCase().includes(q));
  const orderRows = (orders ?? []).filter((o) => matches(o.symbol, o.market, o.side, o.order_type, o.status));
  const requestRows = (requests ?? []).filter((r) => matches(r.symbol, r.market, r.side, r.order_type, r.status));
  const pendingCount = (requests ?? []).filter((r) => r.status === 'PENDING').length;

  return (
    <Box>
      <PageHeader title="Orders" subtitle="Your executed orders and approval requests — live updates" />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Order History" />
          <Tab label={pendingCount ? `Approval Requests (${pendingCount})` : 'Approval Requests'} />
        </Tabs>
        <TextField
          size="small"
          placeholder="Search symbol, side, type, status…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ minWidth: 260 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            endAdornment: query
              ? <InputAdornment position="end"><CloseIcon fontSize="small" sx={{ cursor: 'pointer' }} onClick={() => setQuery('')} /></InputAdornment>
              : undefined,
          }}
        />
      </Box>

      {tab === 0 && cancel.isError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => cancel.reset()}>
          {((cancel.error as AxiosError<{ error?: { message?: string } }>)?.response?.data?.error?.message) || 'Failed to cancel order.'}
        </Alert>
      )}

      {tab === 0 && (
        <Card>
          <TableContainer>
            <Table sx={{ minWidth: 680, '& tbody tr:hover': { backgroundColor: 'rgba(255,255,255,0.03)' } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Symbol</TableCell>
                  <TableCell>Market</TableCell>
                  <TableCell>Side</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Lev</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Filled</TableCell>
                  <TableCell align="right">Avg Price</TableCell>
                  <TableCell align="right">Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orderRows.map((o) => (
                  <TableRow key={o.id}>
                    <TableCell sx={{ fontWeight: 700 }}>{o.symbol}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{o.market}</TableCell>
                    <TableCell sx={{ color: o.side === 'BUY' ? '#16c784' : '#ea3943', fontWeight: 700 }}>{o.side}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{o.order_type}</TableCell>
                    <TableCell align="right" className="mono">{o.leverage ? `${o.leverage}x` : '—'}</TableCell>
                    <TableCell align="right" className="mono">{fmtNum(o.quantity)}</TableCell>
                    <TableCell align="right" className="mono">{fmtNum(o.filled_quantity)}</TableCell>
                    <TableCell align="right" className="mono">{fmtNum(o.avg_fill_price)}</TableCell>
                    <TableCell align="right">
                      <StatusChip status={o.status} title={o.error_message ?? undefined} />
                      {o.status === 'FAILED' && o.error_message && (
                        <Box sx={{ mt: 0.5, ml: 'auto', maxWidth: 240, fontSize: 11, color: '#ea3943', whiteSpace: 'normal', textAlign: 'right' }}>
                          {o.error_message}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {cancellable(o) ? (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={cancel.isPending && cancel.variables === o.id ? <CircularProgress size={14} color="inherit" /> : <CloseIcon />}
                          disabled={cancel.isPending}
                          onClick={() => setPendingCancel(o)}
                        >
                          Cancel
                        </Button>
                      ) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
                {!orderRows.length && (
                  <TableRow>
                    <TableCell colSpan={10} sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                      {q ? 'No orders match your search.' : 'No orders yet.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {tab === 1 && (
        <Card>
          <TableContainer>
            <Table sx={{ minWidth: 720, '& tbody tr:hover': { backgroundColor: 'rgba(255,255,255,0.03)' } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Symbol</TableCell>
                  <TableCell>Market</TableCell>
                  <TableCell>Side</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell>Requested</TableCell>
                  <TableCell align="right">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requestRows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell sx={{ fontWeight: 700 }}>{r.symbol}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{r.market}</TableCell>
                    <TableCell sx={{ color: r.side === 'BUY' ? '#16c784' : '#ea3943', fontWeight: 700 }}>{r.side}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{r.order_type}</TableCell>
                    <TableCell align="right" className="mono">{fmtNum(r.quantity)}</TableCell>
                    <TableCell align="right" className="mono">{fmtNum(r.price)}</TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>{new Date(r.created_at).toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <StatusChip status={r.status} title={r.status === 'REJECTED' && r.reject_reason ? r.reject_reason : undefined} />
                    </TableCell>
                  </TableRow>
                ))}
                {!requestRows.length && (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                      {q ? 'No requests match your search.' : 'No approval requests. Orders you place on approval-required accounts appear here.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <ConfirmDialog
        open={!!pendingCancel}
        title="Cancel order?"
        confirmLabel="Cancel Order"
        cancelLabel="Keep Order"
        confirmColor="error"
        severity="warning"
        loading={cancel.isPending}
        onCancel={() => setPendingCancel(null)}
        onConfirm={() => { if (pendingCancel) cancel.mutate(pendingCancel.id); }}
        warning="This cancels the resting order on the exchange. Any unfilled quantity will not be executed. This cannot be undone."
      >
        {pendingCancel && (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', rowGap: 0.75, columnGap: 2 }}>
            <Typography variant="body2" color="text.secondary">Symbol</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }}>{pendingCancel.symbol} ({pendingCancel.market})</Typography>
            <Typography variant="body2" color="text.secondary">Side / Type</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right', color: pendingCancel.side === 'BUY' ? '#16c784' : '#ea3943' }}>
              {pendingCancel.side} · {pendingCancel.order_type}
            </Typography>
            <Typography variant="body2" color="text.secondary">Quantity</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }} className="mono">{fmtNum(pendingCancel.quantity)}</Typography>
            <Typography variant="body2" color="text.secondary">Filled</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }} className="mono">{fmtNum(pendingCancel.filled_quantity)}</Typography>
          </Box>
        )}
      </ConfirmDialog>
    </Box>
  );
}
